import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from './services/authService';
import { userService } from './services/userService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    return {
      currentUser: null,
      userRole: null,
      loading: false,
      isAuthenticated: !!localStorage.getItem('token'),
      isAdmin: false,
      isAgent: false,
      isEcommerce: false,
      isUser: true,
      setUserRole: () => {},
      completeEmailSignIn: async () => ({ success: false, error: 'Passwordless sign-in is disabled.' }),
      logout: async () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
      }
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeRole = (role) => {
    if (!role || typeof role !== 'string') return null;
    const r = role.trim().toLowerCase();
    const allowed = ['admin', 'agent', 'ecommerce', 'user'];
    return allowed.includes(r) ? r : 'user';
  };

  // Set user role in localStorage and state
  const setUserRoleState = (role) => {
    const normalized = normalizeRole(role) || 'user';
    localStorage.setItem('role', normalized);
    setUserRole(normalized);
  };

  // Clear user session
  const logout = async () => {
    try {
      await authService.signOut();
      setCurrentUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Complete email sign-in
  // Deprecated: passwordless sign-in has been removed
  const completeEmailSignIn = async () => ({ success: false, error: 'Passwordless sign-in is disabled.' });

  useEffect(() => {
    // Initialize auth state from backend token/profile only
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const data = await userService.getMe();
          const backendUser = data?.user || null;
          
          // RECOVERY: If the backend returned a fresh token (role recovery), use it.
          if (data?.token) {
            localStorage.setItem('token', data.token);
          }

          if (backendUser) {
            setCurrentUser(backendUser);
            const finalName = backendUser.name || (backendUser.email ? backendUser.email.split('@')[0] : 'User');
            const prev = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
            const merged = { ...prev, ...backendUser };
            merged.name = finalName;
            localStorage.setItem('user', JSON.stringify(merged));
          }
          const backendRole = normalizeRole(backendUser?.role);
          const storedRole = normalizeRole(localStorage.getItem('role'));
          
          // Determine the final role
          let role = 'user';
          
          // CRITICAL: If the email is the default admin email, force the role to 'admin' 
          // to recover from accidental role changes during testing on localhost.
          const isAdminEmail = backendUser?.email === 'admin@fexlogistics.com';
          
          if (isAdminEmail) {
            role = 'admin';
          } else if (backendRole && backendRole !== 'user') {
            // 1. If backend says they are a privileged user (admin/agent/ecommerce), trust it completely.
            role = backendRole;
          } else if (storedRole && storedRole !== 'user') {
            // 2. If backend says 'user' but we have a stored privileged role, 
            // this handles the race condition during a recent upgrade.
            role = storedRole;
          } else if (backendRole) {
            // 3. Fallback to backend 'user' role
            role = backendRole;
          }
          
          // Ensure consistency
          localStorage.setItem('role', role);
          setUserRole(role);
        } else {
          // No token: derive role from local storage if present
          const storedRole = normalizeRole(localStorage.getItem('role'));
          setUserRole(storedRole || null);
          setCurrentUser(null);
        }
      } catch (err) {
        // On error, assume logged out state
        setCurrentUser(null);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const value = {
    currentUser,
    userRole,
    loading,
    // Authenticated solely by backend token
    isAuthenticated: !!localStorage.getItem('token'),
    isAdmin: userRole === 'admin',
    isAgent: userRole === 'agent',
    isEcommerce: userRole === 'ecommerce',
    isUser: userRole === 'user' || !userRole,
    setUserRole: setUserRoleState,
    completeEmailSignIn,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};